import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../../firebase';
import { useEmailPanel, type PanelEmailData } from '../contexts/EmailPanelContext';
import type { FeedEmailData } from '../components/EmailFeedContent';
import { markEmailRead } from '../utils/markEmailRead';
import {
  parseOpenEmailId,
  parseOpenEmailMessage,
  stripOpenEmailParam,
} from '../../../utils/pushNotifications';

/**
 * Open an email in the sliding mail panel from a push notification.
 *
 * Two entry points, one path:
 *   - cold start: the notification opened '/?openEmail=<emailDocId>'
 *   - app already open: the service worker posts { type: 'OPEN_EMAIL', emailId }
 *
 * Why the id is captured at MODULE load, not in an effect: the app's index
 * route immediately <Navigate>s '/' to the home page, and the login gate can
 * bounce to /auth, both of which drop the query string before any component
 * effect runs. This file is imported statically at bundle start (via
 * PrivateRoutes -> EmailSlidingPanel), so reading location here sees the URL
 * the notification actually opened; the id then waits in memory until the
 * (authenticated) panel mounts.
 */

// Give the live inbox snapshot this long to arrive before reading Firestore
// directly. The snapshot gives the canonical, feed-identical row shape; the
// direct read exists so a slow/failed snapshot can never strand the tap.
const SNAPSHOT_WAIT_MS = 3000;

let pendingBootId: string | null =
  typeof window !== 'undefined' ? parseOpenEmailId(window.location.search) : null;

/** Drop ?openEmail= from the address bar, keeping the router's history.state. */
function stripOpenEmailFromLocation(): void {
  try {
    if (!new URLSearchParams(window.location.search).has('openEmail')) return;
    window.history.replaceState(window.history.state, '', stripOpenEmailParam(window.location.href));
  } catch { /* best effort */ }
}

function toDate(value: any): Date {
  if (value && typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
}

// Last-resort shape for a doc read straight from Firestore (same spread the
// dashboard/event-log openers use), with the few fields the views key off.
function rawDocToPanelEmail(id: string, data: any): PanelEmailData {
  return {
    ...data,
    id,
    emailId: id,
    subject: (typeof data?.subject === 'string' && data.subject.trim()) || 'No Subject',
    createdAt: toDate(data?.date),
  } as PanelEmailData;
}

interface OpenContext {
  preLoadedEmails: FeedEmailData[];
  loadEmailsByIds: (ids: string[]) => Promise<FeedEmailData[]>;
  openEmailPanel: (view: 'viewEmail', data?: PanelEmailData) => void;
}

async function resolveEmail(id: string, ctx: OpenContext): Promise<PanelEmailData | null> {
  const inFeed = ctx.preLoadedEmails.find((e) => e.id === id || (e as any).emailId === id);
  if (inFeed) return inFeed;

  // Same hydrate-and-merge path the search results use.
  try {
    const loaded = await ctx.loadEmailsByIds([id]);
    if (loaded[0]) return loaded[0];
  } catch { /* fall through to the direct read */ }

  // loadEmailsByIds also filters by mail-source mode, so a miss there is not
  // proof the doc is gone: read it directly.
  try {
    const snap = await getDoc(doc(db, 'emails', id));
    if (snap.exists()) return rawDocToPanelEmail(snap.id, snap.data());
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[push] openEmail read failed:', (e as any)?.code || 'error');
  }
  return null;
}

export function useOpenEmailDeepLink(): void {
  const { preLoadedEmails, isLoadingInitialEmails, openEmailPanel, loadEmailsByIds } = useEmailPanel();

  // The context hands out fresh function identities every render; keep the
  // latest in a ref so they never retrigger the effects below.
  const latest = useRef<OpenContext>({ preLoadedEmails, loadEmailsByIds, openEmailPanel });
  latest.current = { preLoadedEmails, loadEmailsByIds, openEmailPanel };

  // One request at a time. A fresh object per call, so asking for the SAME id
  // again (tap a second notification for the same thread) still re-runs.
  const [request, setRequest] = useState<{ id: string; n: number } | null>(null);
  const seq = useRef(0);
  const processing = useRef<number | null>(null);

  const enqueue = useCallback((id: string) => {
    seq.current += 1;
    setRequest({ id, n: seq.current });
  }, []);

  // Cold start: the id captured at module load (or, failing that, the URL now).
  useEffect(() => {
    const id = pendingBootId || parseOpenEmailId(window.location.search);
    pendingBootId = null;
    stripOpenEmailFromLocation();
    if (id) enqueue(id);
  }, [enqueue]);

  // App already open: the service worker's notificationclick focuses this
  // window and posts the email id.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
    const onMessage = (event: MessageEvent) => {
      const id = parseOpenEmailMessage(event.data);
      if (id) enqueue(id);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [enqueue]);

  useEffect(() => {
    if (!request) return;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const run = () => {
      // The effect can re-run (snapshot lands while the wait timer is pending,
      // StrictMode double-invoke): never process the same request twice.
      if (processing.current === request.n) return;
      processing.current = request.n;

      void (async () => {
        try {
          const email = await resolveEmail(request.id, latest.current);
          if (!email) {
            toast.info("Couldn't open that email - it may have been deleted or moved.", { toastId: 'push-open-missing' });
            return;
          }
          // openEmailPanel both opens a closed panel and swaps the view of an
          // open one (the shell effect keys on emailPanelView/currentEmailData).
          latest.current.openEmailPanel('viewEmail', email);
          // Opening from a notification is the same act as opening the row:
          // clear the unread dot (no-op when already read; auto = no toast if
          // the second user is refused). A flagged email stays unread, as in
          // the feed.
          if (!(email as any).pinned) void markEmailRead(email as FeedEmailData, { auto: true });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[push] openEmail failed:', (e as any)?.code || 'error');
        } finally {
          setRequest((cur) => (cur && cur.n === request.n ? null : cur));
        }
      })();
    };

    if (isLoadingInitialEmails) timer = setTimeout(run, SNAPSHOT_WAIT_MS);
    else run();

    return () => { if (timer) clearTimeout(timer); };
  }, [request, isLoadingInitialEmails]);
}
